import { notifications } from '@mantine/notifications';
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  usePrepareTransactionRequest,
  useSendTransaction,
  useWaitForTransactionReceipt,
} from 'wagmi';
import { CallData } from 'src/types';

export function usePrepareAndSendTransaction({
  callData,
  successNotificationMessage,
  refetchOnSuccess,
  onSuccess,
}: {
  callData?: CallData;
  successNotificationMessage?: React.ReactNode;
  refetchOnSuccess?: boolean;
  onSuccess?: (hash: string) => void;
}) {
  const queryClient = useQueryClient();

  const {
    data: transactionRequest,
    isLoading: isPreparingTransaction,
    error: transactionRequestError,
  } = usePrepareTransactionRequest({
    to: callData?.to,
    data: callData?.data,
    value: BigInt(callData?.value === '0x00' ? '0' : (callData?.value ?? '0')),
    account: callData?.from,
    gas: BigInt(callData?.gasLimit ?? '0'),
    query: {
      enabled: !!callData,
    },
  });

  const {
    data: hash,
    isPending: isSending,
    error: sendError,
    sendTransaction,
  } = useSendTransaction();

  const confirmationEnabled = !!hash;

  const {
    isLoading: isConfirming,
    isSuccess,
    error: confirmError,
  } = useWaitForTransactionReceipt({
    hash,
    query: { enabled: confirmationEnabled },
  });

  useEffect(() => {
    if (isSuccess) {
      if (successNotificationMessage) {
        notifications.show({
          message: successNotificationMessage,
        });
      }

      if (refetchOnSuccess) {
        queryClient.refetchQueries();
      }

      onSuccess?.(hash!);
      console.log('Transaction completed:', transactionRequest);
    }
  }, [isSuccess]);

  return {
    isPreparingTransaction,
    isSending: isSending || isConfirming,
    transactionError:
      transactionRequestError ||
      sendError ||
      (confirmationEnabled ? confirmError : undefined),
    sendTransaction: transactionRequest
      ? () => {
          sendTransaction(transactionRequest);
        }
      : undefined,
  };
}
